import { motion } from "framer-motion";

const STEPS = [
  {
    number: "01",
    title: "無料相談・現場DX診断",
    description: "現状の業務フローと課題をヒアリング。ボトルネックを特定し、AI自動化の適用可能性を診断します。",
    duration: "約60分",
  },
  {
    number: "02",
    title: "要件定義・お見積り",
    description: "自動化対象の業務範囲とゴールを定義。最適なサービスプランと、補助金の活用可能性をご提案します。",
    duration: "1〜2週間",
  },
  {
    number: "03",
    title: "開発・テスト",
    description: "AntigravityとWorkspace MCPを活用してAIエージェントを開発。お客様の実データでテストを実施します。",
    duration: "2〜4週間",
  },
  {
    number: "04",
    title: "実装・レクチャー",
    description: "開発したエージェントを実際の業務環境に実装。担当者様への操作レクチャーと運用マニュアルを提供します。",
    duration: "1週間",
  },
  {
    number: "05",
    title: "運用定着・改善サイクル",
    description: "実装後も定期的なレビューと改善を実施。現場の声を反映し、自動化の範囲を段階的に拡大します。",
    duration: "継続的",
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function StepsSection() {
  return (
    <section id="steps" className="py-14 sm:py-20 lg:py-28 bg-white">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-16">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Process
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            実装ステップ
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-2xl leading-relaxed">
            無料相談から運用定着まで、一気通貫で伴走します。「何から始めればいいか分からない」——その一言から、一緒に考えます。
          </p>
        </motion.div>

        {/* Steps Timeline */}
        <div className="relative">
          {/* Vertical line (desktop) */}
          <div className="hidden lg:block absolute left-[39px] top-0 bottom-0 w-px bg-[#E0E0E0]" />
          {/* Vertical line (mobile) */}
          <div className="lg:hidden absolute left-[27px] sm:left-[39px] top-0 bottom-0 w-px bg-[#E0E0E0]" />

          <div className="space-y-4 sm:space-y-6 lg:space-y-0">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.number}
                {...fadeInUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative flex gap-4 sm:gap-6 lg:gap-10 group"
              >
                {/* Number circle */}
                <div className="shrink-0 relative z-10">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 flex items-center justify-center bg-white border-2 border-[#E0E0E0] group-hover:border-[#E03030] transition-colors duration-300">
                    <span
                      className="text-lg sm:text-2xl font-black text-[#E0E0E0] group-hover:text-[#E03030] transition-colors duration-300"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {step.number}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 pb-6 sm:pb-10 lg:pb-14 pt-2 sm:pt-3">
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 mb-1.5 sm:mb-2">
                    <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A]">
                      {step.title}
                    </h3>
                    <span className="text-[10px] sm:text-xs font-medium text-[#666666] bg-[#F0F0F0] px-2 py-0.5 rounded-sm self-start" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {step.duration}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-[#333333] leading-relaxed max-w-xl">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
